import React, {FC, useMemo, useState} from "react";
import {grey} from "@material-ui/core/colors";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import DataRow from "view/components/DataRow";
import InfoClickTooltip from "view/components/InfoClickTooltip";
import {
  IBuildType,
  IPackage,
  IStand,
  TaskPackageMode,
} from "@evennt-tech/api-v2-core";
import {DocumentReference} from "api/firebase";
import {PackageMode} from "view/components/TaskDrawer/AssignToRow/shared";
import DomainIcon from "@material-ui/icons/Domain";
import FlexBox from "view/components/Layout/FlexBox";
import {Chip, InputBase, Popover, Tooltip} from "@material-ui/core";
import {ExcludePackageIcon, IncludePackageIcon} from "assets/package";
import BuildIcon from "@material-ui/icons/Build";
import useStyles from "./styles";

type AssignToRowProps = {
  buildTypes?: string[];
  packageIds?: string[];
  stands?: string[];
  buildTypesInShow: IBuildType[];
  packagesInShow: IPackage[];
  newStands: IStand[];
  standsInShowCount?: number;
  packageMode?: PackageMode | TaskPackageMode | null;
  onEditAssignTo: () => void;
  disabled?: boolean;
  isConditional?: boolean;
};

const AssignToRow: FC<AssignToRowProps> = ({
  buildTypes,
  packageIds,
  stands,
  buildTypesInShow,
  packagesInShow,
  newStands,
  standsInShowCount,
  packageMode,
  onEditAssignTo,
  disabled,
  isConditional,
}) => {
  const displayValue = useMemo(() => {
    // A conditional CHILD has no assignment of its own — visibility is
    // governed solely by the parent task's answer (the BE visibility
    // resolver bypasses build-type / package / stand filters for it).
    if (isConditional) return "N/A";

    const parts: React.ReactNode[] = [];

    if (stands?.length) {
      // `stands` carries UUID `IStand.id` values, so match on `newStand.id`.
      const standNames =
        standsInShowCount !== undefined && standsInShowCount === stands.length
          ? ["All"]
          : newStands.reduce<string[]>((acc, newStand) => {
              if (stands.find((id) => id === newStand.id)) {
                acc.push(newStand.name);
              }
              return acc;
            }, []);
      parts.push(
        <StyledChip
          icon={<DomainIcon htmlColor={grey[600]} style={{fontSize: "18px"}} />}
          title="Exhibitor"
          popoverTitle="Stands"
          value={standNames}
        />
      );
    }

    if (buildTypes?.length && !isConditional) {
      const buildTypeNames =
        buildTypesInShow?.length === buildTypes.length
          ? ["All"]
          : (buildTypes
              .map((id) => buildTypesInShow.find((bt) => bt.id === id)?.name)
              .filter(Boolean) as string[]);
      parts.push(
        <StyledChip
          icon={<BuildIcon htmlColor={grey[600]} style={{fontSize: "18px"}} />}
          title="Build Type"
          popoverTitle="Build Types"
          value={buildTypeNames}
        />
      );
    }

    if (
      packageIds?.length &&
      packageMode !== PackageMode.NONE &&
      !isConditional
    ) {
      const packageNames =
        packagesInShow.length === packageIds.length
          ? ["All"]
          : (packageIds
              .map((id) => packagesInShow.find((pkg) => pkg.id === id)?.name)
              .filter(Boolean) as string[]);
      const title =
        packageMode === PackageMode.ASSIGN ? "Package" : "Excl. Package";
      const icon =
        packageMode === PackageMode.ASSIGN ? (
          <IncludePackageIcon htmlColor={grey[500]} />
        ) : (
          <ExcludePackageIcon htmlColor={grey[500]} />
        );

      parts.push(
        <StyledChip
          icon={icon}
          title={title}
          popoverTitle={
            packageMode === PackageMode.ASSIGN
              ? "Packages"
              : "Excluded Packages"
          }
          value={packageNames}
        />
      );
    }

    return parts.length ? (
      <FlexBox gap={1} alignCenter style={{flexWrap: "wrap"}} pt={1} pb={1}>
        {parts}
      </FlexBox>
    ) : (
      "-"
    );
  }, [
    isConditional,
    stands,
    buildTypes,
    packageMode,
    packageIds,
    newStands,
    standsInShowCount,
    buildTypesInShow,
    packagesInShow,
  ]);

  return (
    <DataRow
      label="Assign to"
      value={displayValue}
      icon={<PersonAddIcon htmlColor={grey[500]} />}
      actionPanel={
        isConditional ? (
          // Click-only hint (no hover) — same universal component as the
          // conditional-links banner rows.
          <InfoClickTooltip title="The assignment of child tasks is managed by the parent task." />
        ) : (
          <IconButton
            onClick={onEditAssignTo}
            style={{padding: 8}}
            disabled={disabled}
          >
            <EditIcon htmlColor={grey[500]} />
          </IconButton>
        )
      }
    />
  );
};

/** Lists longer than this get a filter input inside the popover. */
const POPOVER_FILTER_THRESHOLD = 15;

/** Hover tooltip shows at most this many rows; the tail collapses into
 *  "…and N more" (the full list lives in the click popover). */
const TOOLTIP_MAX_ROWS = 20;

const composeTooltip = (rows: string[]) => {
  const visible = rows.slice(0, TOOLTIP_MAX_ROWS);
  const hiddenCount = rows.length - visible.length;
  return (
    <>
      {visible.map((row, index) => (
        <React.Fragment key={`${row}-${index}`}>
          {row}
          <br />
        </React.Fragment>
      ))}
      {hiddenCount > 0 && <>…and {hiddenCount} more (click to see all)</>}
    </>
  );
};

type StyledChipProps = {
  title: string;
  /** Popover header, e.g. "Stands" — the chip title names the FILTER
   *  ("Exhibitor"), the popover names the LISTED entities. */
  popoverTitle: string;
  value: string[];
  icon: React.ReactNode;
};

/**
 * Assignment chip. The full value list opens in a click popover (scrollable,
 * filterable) — a hover tooltip with hundreds of stand numbers used to grow
 * into a one-column strip spanning the whole viewport.
 */
export const StyledChip = ({
  title,
  popoverTitle,
  value,
  icon,
}: StyledChipProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState("");

  // The popover only earns its click when the hover tooltip truncates;
  // short lists keep a plain non-clickable chip.
  const hasPopover = value.length > TOOLTIP_MAX_ROWS;

  const openPopover = (event: React.MouseEvent<HTMLElement>) => {
    setFilter("");
    setAnchorEl(event.currentTarget);
  };
  const closePopover = () => setAnchorEl(null);

  const normalizedFilter = filter.trim().toLowerCase();
  const visibleValues = normalizedFilter
    ? value.filter((name) => name.toLowerCase().includes(normalizedFilter))
    : value;

  return (
    <>
      <Tooltip title={anchorEl ? "" : composeTooltip(value)}>
        <Chip
          className={classes.chip}
          clickable={hasPopover}
          onClick={hasPopover ? openPopover : undefined}
          label={
            <FlexBox alignCenter gap={1}>
              <FlexBox alignCenter gap={0.75}>
                {icon}
                <span className={classes.chipLabel}>{title}</span>
              </FlexBox>
              <span className={classes.chipValue}>{value.join(", ")}</span>
            </FlexBox>
          }
        />
      </Tooltip>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
        transformOrigin={{vertical: "top", horizontal: "left"}}
        classes={{paper: classes.valuesPopover}}
      >
        <FlexBox alignCenter justifyContent="space-between" gap={1}>
          <span className={classes.valuesPopoverTitle}>
            {popoverTitle} ({value.length})
          </span>
          <IconButton size="small" onClick={closePopover}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </FlexBox>
        {value.length > POPOVER_FILTER_THRESHOLD && (
          <InputBase
            className={classes.valuesPopoverFilter}
            placeholder="Filter…"
            value={filter}
            autoFocus
            fullWidth
            startAdornment={
              <SearchIcon
                fontSize="small"
                htmlColor={grey[500]}
                style={{marginRight: 4}}
              />
            }
            onChange={(event) => setFilter(event.target.value)}
          />
        )}
        <div className={classes.valuesPopoverList}>
          {visibleValues.length ? (
            visibleValues.map((name, index) => (
              <span
                key={`${name}-${index}`}
                className={classes.valuesPopoverItem}
              >
                {name}
              </span>
            ))
          ) : (
            <span className={classes.valuesPopoverEmpty}>No matches</span>
          )}
        </div>
      </Popover>
    </>
  );
};

export default AssignToRow;
